import { Fireworks } from 'fireworks-js'
import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'
import styled from 'styled-components'

import { Spinner } from '../../App/Components'
import { PublicFooter } from '../components/PublicFooter'
import { StatSquare } from '../components/StatSquare'
import { FireWorkOverlay, Logo, Container, PageWrapper, Title } from '../style'
import {
  transformPublicStatData,
  useGetAnonymousEstablishmentStatsQuery,
  useGetOnlineBookingInfoQuery,
} from '../util/useEstablishmentStatsQuery'

const PageWrapperPublic = styled(PageWrapper)`
  position: relative;
`

export const PublicEstablishmentStats: React.FC = () => {
  const [node, setNode] = useState<HTMLDivElement | null>(null)

  const { t, i18n } = useTranslation()

  const { establishmentUuid } = useParams<{ establishmentUuid: string }>()
  const { data, status } =
    useGetAnonymousEstablishmentStatsQuery(establishmentUuid)

  const onlineBookingInfo = useGetOnlineBookingInfoQuery(establishmentUuid)

  const refCallback = (element: HTMLDivElement) => setNode(element)

  useEffect(() => {
    if (node) {
      const fireworks = new Fireworks(node)
      fireworks.start()

      const timeoutId = setTimeout(() => {
        fireworks.stop(true)
      }, 12000)

      return () => clearTimeout(timeoutId)
    }
  }, [node])

  useEffect(() => {
    if (onlineBookingInfo.data) {
      const establishmentLanguage = onlineBookingInfo.data.language
      const validLanguages = ['nl', 'fr', 'en']
      if (validLanguages.includes(establishmentLanguage)) {
        i18n.changeLanguage(establishmentLanguage)
      } else {
        i18n.changeLanguage('en')
      }
    }
  }, [onlineBookingInfo.data])

  if (status === 'pending' || onlineBookingInfo.status === 'pending') {
    return <Spinner />
  }

  if (status === 'error' || onlineBookingInfo.status === 'error') {
    return <p>{t('Something went wrong')}</p>
  }

  const statsEntries = transformPublicStatData(data._resource)

  const establishmentId = onlineBookingInfo.data.id
  const bookingUrl = `https://booking.optios.net/${establishmentId}/`
  const establishmentMetaData = onlineBookingInfo.data.locations.find(
    location => location.id === establishmentId,
  )

  const locationName = establishmentMetaData?.name
  const locationLogo = establishmentMetaData?.image

  const statsDate = data._meta.query_filters.starts_at.substring(0, 4)

  return (
    <PageWrapperPublic>
      <FireWorkOverlay ref={refCallback} />
      <Title>{`${statsDate} ${t('year_in_location')} ${locationName}`}</Title>
      {locationLogo ? <Logo src={locationLogo} alt={t('Logo')} /> : null}
      <Container>
        {statsEntries.map(stats => (
          <StatSquare key={stats.key} data={stats} refData={statsEntries} />
        ))}
      </Container>
      <PublicFooter
        bookingUrl={bookingUrl}
        referralUrl={onlineBookingInfo.data.referral_url}
      />
    </PageWrapperPublic>
  )
}
